import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  LocationOn,
  Flag,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import {
  getAPIProgressData,
  getApiData,
  getApiDataWith403Handling,
} from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import { updateStepMappingToCompleted } from "../../utils/stepMappingUtils";
import authActions from "../../redux/reducers/auth/actions";
import PlaceAutoComplete from "../../components/PlaceAutoComplete";
import CInput from "../../components/CInput";
import CPhoneInput from "../../components/CPhoneInput";
import ProfilePhotoModal from "../../components/ProfilePhotoModal";

export default function EditProfileForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setUserData } = authActions;
  const { token, userData } = useSelector((state) => state.auth);
  
  const [buttonLoader, setButtonLoader] = useState(false);
  const [pageLoad, setPageLoad] = useState(true);
  const [profileData, setProfileData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({});
  const [userLocation, setUserLocation] = useState("");
  const [bLogo, setBLogo] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    businessLogo: "",
    cname: "",
    address: "",
    email: "",
    website: "",
    phone: "",
    countryCode: "",
    pCode: "",
    businessYear: "",
    employees: "",
    annualContract: "",
    description: "",
    username: "",
    type: "",
  });

  const [errors, setErrors] = useState({
    addErr: false,
    addMsg: "",
    webErr: false,
    webMsg: "",
    yearErr: false,
    yearMsg: "",
    employeeErr: false,
    employeeMsg: "",
    contractErr: false,
    contarctMsg: "",
    roleErr: false,
    roleMsg: "",
    websiteErr: false,
    websiteMsg: "",
  });

  const data = profileData?.contractor_data;
  const isEdit = !isEmpty(profileData);

  useEffect(() => {
    getUserDetailsByIdApiCall();
  }, []);

  useEffect(() => {
    if (isEdit && profileData?.contractor_data) {
      const contractorData = profileData.contractor_data;
      
      // Map API data to form fields
      setFormData({
        businessLogo: profileData?.profile_url || "",
        cname: contractorData?.company_name || "",
        address: contractorData?.company_address || "",
        email: profileData?.email || "",
        website: contractorData?.website || "",
        phone: profileData?.phone_no || "",
        countryCode: "",
        pCode: profileData?.phone_code || "",
        businessYear: contractorData?.total_years_in_business || "",
        employees: contractorData?.company_size || "",
        annualContract: contractorData?.no_of_contracts_annually || "",
        description: contractorData?.description || "",
        username: profileData?.username || "",
        type: contractorData?.type || userData?.role || "",
      });

      // Set location data from API
      if (contractorData?.lat && contractorData?.long) {
        let locationObj = {
          lat: contractorData.lat.toString(),
          long: contractorData.long.toString(),
          location: contractorData?.company_address || "",
          city: contractorData?.city || ""
        };
        setSelectedLocation(locationObj);
        setUserLocation(locationObj?.location);
      }
    }
  }, [profileData, isEdit]);

  useEffect(() => {
    if (formData?.businessLogo && typeof formData?.businessLogo === "object") {
      const imgUrl = URL.createObjectURL(formData?.businessLogo);
      setBLogo(imgUrl);
    } else {
      setBLogo(formData?.businessLogo || "");
    }
  }, [formData?.businessLogo]);

  async function getUserDetailsByIdApiCall() {
    setPageLoad(true);
    try {
      const response = await getApiData(
        Setting.endpoints.me,
        "GET",
        null,
        { Authorization: `Bearer ${token}` }
      );
      if (response.success) {
        dispatch(setUserData(response?.data));
        setProfileData(response?.data);
      } else {
        toast.error("Failed to load profile data");
      }
      setPageLoad(false);
    } catch (error) {
      console.log("🚀 ~ file: index.js:63 ~ by id api ~ error:", error);
      toast.error("Failed to load profile data");
      setPageLoad(false);
    }
  }

  function validation() {
    const error = { ...errors };
    let valid = true;
    let scroll = false;
    let section = null;

    // Company name validation removed - field is now disabled

    if (formData.description && formData.description.length > 1000) {
      valid = false;
      error.descriptionErr = true;
      error.descriptionMsg = "Description should not be greater than 1000 characters";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#description");
      }
    }

    if (isEmpty(userLocation)) {
      valid = false;
      error.addErr = true;
      error.addMsg = "Please Enter Address";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#Address");
      }
    }

    if (isEmpty(formData.businessYear)) {
      toast.error("Please select the number of business years");
      valid = false;
    }

    if (userData?.role !== "designer" && isEmpty(formData.employees)) {
      toast.error("Please select the number of employees");
      valid = false;
    }

    if (isEmpty(formData.annualContract)) {
      valid = false;
      error.contractErr = true;
      error.contarctMsg = "Please Enter No. of Contracts Annually";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#contract");
      }
    } else if (formData?.annualContract < 0) {
      valid = false;
      error.contractErr = true;
      error.contarctMsg = "Please Enter Valid No. of Contracts Annually";
      if (!scroll) {
        scroll = true;
        section = document.querySelector("#contract");
      }
    }

    // Role validation removed - field is now disabled

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setErrors(error);

    if (valid) {
      editContractorDetailsApiCall();
    }
  }

  async function updateUserProfileApiCall() {
    try {
      const usernameToSend = formData.username || profileData?.username || "";
      
      const updateData = {
        username: usernameToSend,
      };
      
      if (formData.phone) updateData.phone_no = formData.phone;
      if (formData.pCode) updateData.phone_code = formData.pCode;

      const response = await getApiData(
        Setting.endpoints.updateProfile,
        "POST",
        updateData,
        { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      );

      if (response.success) {
        console.log("User profile updated successfully:", response);
        return true;
      } else {
        console.log("Failed to update user profile:", response);
        toast.error(response.message || "Failed to update user profile");
        return false;
      }
    } catch (error) {
      console.log("Error updating user profile:", error);
      toast.error("Failed to update user profile");
      return false;
    }
  }

  async function editContractorDetailsApiCall() {
    try {
      setButtonLoader(true);
      
      const userProfileUpdated = await updateUserProfileApiCall();
      if (!userProfileUpdated) {
        setButtonLoader(false);
        return;
      }

      let data = {
        description: formData?.description ? formData?.description : "",
        company_address: userLocation ? userLocation : "",
        website: formData?.website ? formData?.website : "",
        total_years_in_business: formData?.businessYear ? formData?.businessYear : "",
        company_size: formData?.employees ? formData?.employees : "",
        no_of_contracts_annually: formData?.annualContract ? parseInt(formData?.annualContract) : 0,
        contractor_expertise: userData?.contractor_data?.contractor_expertise || "",
        linkedin_url: userData?.contractor_data?.linkedin_url || "",
        fb_url: userData?.contractor_data?.fb_url || "",
        insta_url: userData?.contractor_data?.insta_url || "",
        lat: selectedLocation?.lat ? parseFloat(selectedLocation?.lat) : null,
        long: selectedLocation?.long ? parseFloat(selectedLocation?.long) : null,
        city: selectedLocation?.city || "",
        type: formData?.type || "contractor",
        projects_key_locations: userData?.contractor_data?.projects_key_locations || "",
        company_name: userData?.contractor_data?.company_name || "",
      };
      console.log("API payload:", data);

      const response = await getAPIProgressData(
        Setting.endpoints.addContractorDetails,
        "POST",
        data,
        true,
        { Authorization: `Bearer ${token}` }
      );
      
      if (response.success) {
        await updateStepMappingToCompleted(userData?.id || profileData?.id, "CREATE_ACCOUNT", token);
        
        await getUserDetailsByIdApiCall();
        
        toast.success("Profile updated successfully");
        navigate(-1);
      } else {
        toast.error(response.message);
      }
      setButtonLoader(false);
    } catch (error) {
      console.log("🚀 ~ file: index.js:63 ~ loginUser ~ error:", error);
      setButtonLoader(false);
      toast.error(error.toString());
    }
  }

  async function initiateContractorOnboardingWorkflow() {
    try {
      const refId = userData?.id || profileData?.id;
      
      if (!refId) {
        console.log("No valid refId found for contractor onboarding workflow");
        return;
      }
      
      const stepsMapData = {
        refId: parseInt(refId),
        refType: "contractor",
        workflowName: "CONTRACTOR_ONBOARDING"
      };
      
      const response = await getApiData(
        Setting.endpoints.stepsMap,
        "POST",
        stepsMapData,
        { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        true // Use new base URL for steps mapping
      );
      
      if (response.success) {
        // Now update the CREATE_ACCOUNT step to COMPLETED
        await updateStepMappingToCompleted(refId, "CREATE_ACCOUNT", token);
        
        toast.success("Getting started process initiated successfully");
      } else {
        console.log("Failed to initiate contractor onboarding workflow:", response);
        // Don't show error toast as this is not critical for profile update
      }
    } catch (error) {
      console.log("Error initiating contractor onboarding workflow:", error);
      console.log("Error details:", error.message, error.response);
      
      // Handle specific error cases
      if (error.message && error.message.includes("WORKFLOW_ALREADY_EXISTS")) {
        console.log("Workflow already exists for this contractor - this is expected behavior");
        // Don't show error toast as this is not critical for profile update
      } else {
        console.log("Unexpected error during workflow initiation");
        // Don't show error toast as this is not critical for profile update
      }
    }
  }


  // Website validation function
  const validateWebsite = (url) => {
    if (!url) return { isValid: true, message: "" };
    
    // Basic URL format validation
    const urlRegex = /^https?:\/\/(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
    
    if (!urlRegex.test(url)) {
      return { 
        isValid: false, 
        message: "Invalid website URL. Must include http:// or https:// and a valid domain" 
      };
    }
    
    return { isValid: true, message: "" };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate website field
    if (field === "website") {
      const validation = validateWebsite(value);
      setErrors(prev => ({
        ...prev,
        websiteErr: !validation.isValid,
        websiteMsg: validation.message
      }));
    }
  };

  const handleLocationChange = (locationObj) => {
    console.log("Location selected:", locationObj);
    // Map PlaceAutoComplete data structure to our expected structure
    const mappedLocation = {
      lat: locationObj?.lat,
      long: locationObj?.lng, // PlaceAutoComplete uses 'lng', we need 'long'
      location: locationObj?.location,
      city: locationObj?.city
    };
    console.log("Mapped location:", mappedLocation);
    setSelectedLocation(mappedLocation);
    setUserLocation(locationObj?.location);
    setFormData(prev => ({ ...prev, address: locationObj?.location }));
  };

  const businessYearsOptions = [
    "0-1 year",
    "2-3 years", 
    "4-7 years",
    "8-15 years",
    "16+ years"
  ];

  const companySizeOptions = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "200+ employees"
  ];

  if (pageLoad) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Gradient Header */}
      <div
        style={{
          padding: "4vh 2vw 2vh 2vw",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton 
              onClick={() => navigate(-1)} 
              sx={{ 
                p: 1,
                border: "1px solid #E5E7EB",
                borderRadius: "50%",
                backgroundColor: "white",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                }
              }}
            >
              <ArrowBack sx={{ fontSize: 24 }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: "600",
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                letterSpacing: "0",
              }}
            >
              Edit Profile
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={validation}
            disabled={buttonLoader}
            sx={{
              backgroundColor: "#4D6BF6",
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1.5,
              height: "fit-content",
            }}
          >
            {buttonLoader ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </div>


      {/* Form Content */}
      <Box sx={{ p: 3 }}>
        {/* Personal Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1F2937" }}>
            Personal Information
          </Typography>
          
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            {/* Profile Avatar */}
            <Box sx={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
              <Avatar
                src={formData.businessLogo || bLogo}
                sx={{
                  width: 100,
                  height: 100,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  fontSize: "2rem",
                }}
              >
                {formData.username?.charAt(0) || "U"}
              </Avatar>
              <IconButton
                onClick={() => setPhotoModalOpen(true)}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "2px solid white",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <Edit sx={{ fontSize: 16, color: "#4D6BF6" }} />
              </IconButton>
            </Box>

            {/* Personal Info Fields */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Full name and Role dropdown in same row */}
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                  <Box sx={{ width: 400 }}>
                    <CInput
                      label="Username*"
                      value={formData.username || ""}
                      onChange={(e) => {
                        handleInputChange("username", e.target.value);
                      }}
                      placeholder="Enter your username"
                    />
                  </Box>
                  
                  <Box sx={{ width: 400 }}>
                    <CInput
                      label="Role"
                      value={formData.type ? formData.type.charAt(0).toUpperCase() + formData.type.slice(1) : ""}
                      disabled={true}
                      placeholder="Role"
                    />
                  </Box>
                </Box>
                
                {/* Email and WhatsApp number in same row */}
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                  <Box sx={{ width: 400 }}>
                    <CInput
                      label="Email Address"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="Enter your email address"
                      type="email"
                    />
                  </Box>

                  <Box sx={{ width: 400 }}>
                    <CPhoneInput
                      label="WhatsApp Number"
                      placeholder="Enter your WhatsApp number"
                      state={formData}
                      setState={(newState) => {
                        setFormData(prev => ({
                          ...prev,
                          ...newState
                        }));
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Company Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1F2937" }}>
            Company Information
          </Typography>
          
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
            <Box sx={{ width: 400 }}>
              <CInput
                label="Company Name"
                placeholder="Enter your company name"
                value={formData.cname}
                onChange={(e) => handleInputChange("cname", e.target.value)}
                disabled={true}
              />
            </Box>
            
            <Box sx={{ width: 400 }}>
              <CInput
                label="Company Website"
                placeholder="Enter your company website (e.g., https://example.com)"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                type="url"
                error={errors.websiteErr}
                helperText={errors.websiteMsg}
              />
            </Box>
          </Box>
        </Box>

        {/* Location */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1F2937" }}>
            Location
          </Typography>
          
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Box sx={{ width: 400 }}>
                <PlaceAutoComplete
                  placeholder="Enter your office location"
                  onChange={handleLocationChange}
                  defaultValue={userLocation || data?.company_address}
                  error={errors.addErr}
                  helperText={errors.addMsg}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Total Years in Business */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1F2937" }}>
            Total years in business
          </Typography>
          
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {businessYearsOptions.map((option) => (
              <Button
                key={option}
                variant={formData.businessYear === option ? "contained" : "outlined"}
                onClick={() => handleInputChange("businessYear", option)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  backgroundColor: formData.businessYear === option ? "#1F2937" : "white",
                  color: formData.businessYear === option ? "white" : "#6B7280",
                  border: "1px solid #D1D5DB",
                  "&:hover": {
                    backgroundColor: formData.businessYear === option ? "#374151" : "#F9FAFB",
                    border: "1px solid #D1D5DB",
                  },
                }}
              >
                {option}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Company Size */}
        {userData?.role !== "designer" && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1F2937" }}>
              Company size*
            </Typography>
            
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {companySizeOptions.map((option) => (
                <Button
                  key={option}
                  variant={formData.employees === option ? "contained" : "outlined"}
                  onClick={() => handleInputChange("employees", option)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    backgroundColor: formData.employees === option ? "#1F2937" : "white",
                    color: formData.employees === option ? "white" : "#6B7280",
                    border: "1px solid #D1D5DB",
                    "&:hover": {
                      backgroundColor: formData.employees === option ? "#374151" : "#F9FAFB",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Profile Photo Upload Modal */}
      <ProfilePhotoModal
        visible={photoModalOpen}
        handleClose={() => setPhotoModalOpen(false)}
        currentPhoto={formData.businessLogo || bLogo}
        username={formData.username}
        onUploadSuccess={(updatedUserData) => {
          setProfileData(updatedUserData);
          setFormData(prev => ({
            ...prev,
            businessLogo: updatedUserData?.profile_url || "",
          }));
        }}
      />
    </Box>
  );
}
